import { useCallback, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation, getCookieStorage } from '@shared/core';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useToast } from '@withjoy/joykit';
import {
  useGuestIdentificationSessionByGuestKeyLazyQuery,
  useSendGuestIdentificationEmailMutation,
  useVerifyGuestEmailCodeMutation,
} from '../GuestIdentification.graphql.generated';
import { addError } from '@shared/utils/logger';
import type { OtpStepFields } from '../GuestIdentification.types';

type UseOtpStepControllerProps = Readonly<{
  eventId: string;
  email: string;
  onSuccess: (firstName: string | null | undefined, sessionEstablished: boolean) => void;
}>;

const RESEND_COOLDOWN_MS = 30_000;
const SESSION_FETCH_TIMEOUT_MS = 3000;

// Root query fields whose data depends on guest identity. Evicting these (and
// running gc) lets Apollo auto-refetch only currently-mounted observers with
// their own valid variables — avoids the string-based refetchQueries footgun
// where skipped queries get fired with invalid variables.
//
// Coverage:
// - eventByName: GuestIdentificationEligibility, GetEventCTAButtonsData,
//   EventWebsiteGuest, BasicEventInfo, and the eventByName portion of
//   GetProScheduleList
// - getGuestGeneralEventScheduleForEvent: schedule items for general events
// - getGuestForm: Registration V2 (hiddenQuestionIds)
const IDENTITY_DEPENDENT_FIELDS = ['eventByName', 'getGuestGeneralEventScheduleForEvent', 'getGuestForm'] as const;

const evictIdentityFields = (apolloClient: ApolloClient<unknown>) => {
  IDENTITY_DEPENDENT_FIELDS.forEach(fieldName => {
    apolloClient.cache.evict({ fieldName });
  });
  apolloClient.cache.gc();
};

export const useOtpStepController = ({ eventId, email, onSuccess }: UseOtpStepControllerProps) => {
  const { tq } = useTranslation('guestUnlock');
  const { toast } = useToast();
  const apolloClient = useApolloClient();
  const cookieStorage = getCookieStorage();

  const [verifyCode] = useVerifyGuestEmailCodeMutation();
  const [sendCode] = useSendGuestIdentificationEmailMutation();

  const [resending, setResending] = useState(false);
  const lastResendRef = useRef(0);
  const sessionPromiseRef = useRef<{ resolve: () => void; reject: (error: Error) => void } | null>(null);

  const [getSession] = useGuestIdentificationSessionByGuestKeyLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      if (cookieStorage && data.getRecipientSessionByGuestKey) {
        cookieStorage.setItem('_session_id', data.getRecipientSessionByGuestKey);
      }
      if (sessionPromiseRef.current) {
        sessionPromiseRef.current.resolve();
      } else {
        // Timeout already fired and the main flow already evicted (without a
        // session, so mounted queries refetched anonymously). The cookie just
        // arrived late — re-evict so those queries refetch with the now-valid
        // session and the page picks up identity-aware data.
        evictIdentityFields(apolloClient);
      }
    },
    onError: error => {
      sessionPromiseRef.current?.reject(error);
    },
  });

  const waitForSession = useCallback(
    (guestKey: string): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          if (sessionPromiseRef.current) {
            sessionPromiseRef.current = null;
            reject(new Error('Session fetch timeout'));
          }
        }, SESSION_FETCH_TIMEOUT_MS);

        const settle = () => clearTimeout(timeoutId);
        sessionPromiseRef.current = {
          resolve: () => {
            settle();
            resolve();
          },
          reject: (error: Error) => {
            settle();
            sessionPromiseRef.current = null;
            reject(error);
          },
        };

        getSession({ variables: { guestKey, eventId } });
      }),
    [eventId, getSession],
  );

  const formik = useFormik<OtpStepFields>({
    initialValues: {
      code: '',
    },
    validationSchema: Yup.object<OtpStepFields>({
      code: Yup.string()
        .trim()
        .matches(/^\d{6}$/, tq`Code must be 6 digits`)
        .required(tq`Verification code is required`),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      try {
        const response = await verifyCode({
          variables: { eventId, email, code: values.code.trim() },
        });

        const result = response.data?.verifyGuestEmailCode;
        const guestKey = result?.guestKey;

        if (!result?.success || !guestKey) {
          formik.setFieldError('code', tq`Invalid or expired code. Please try again.`);
          return;
        }

        // Establish the session cookie via the existing guest-key flow.
        // If this times out, the late-arrival case is handled in onCompleted:
        // it re-evicts so mounted queries refetch with the now-valid session.
        let sessionEstablished = false;
        try {
          await waitForSession(guestKey);
          sessionEstablished = true;
        } catch (error) {
          addError(error, { context: 'OtpStep/getSession', eventId });
        }

        evictIdentityFields(apolloClient);

        onSuccess(result.firstName, sessionEstablished);
      } catch (error) {
        addError(error, { context: 'OtpStep/verifyGuestEmailCode', eventId });
        formik.setFieldError('code', tq`Something went wrong. Please try again.`);
      }
    },
  });

  const handleResend = useCallback(async () => {
    const now = Date.now();
    if (now - lastResendRef.current < RESEND_COOLDOWN_MS) return;
    lastResendRef.current = now;

    setResending(true);
    try {
      await sendCode({ variables: { eventId, email } });
      toast(tq`Code resent.`, { position: 'bottom-center' });
    } catch (error) {
      addError(error, { context: 'OtpStep/resendCode', eventId });
      toast(tq`Failed to resend code. Please try again.`, { position: 'bottom-center' });
    } finally {
      setResending(false);
    }
  }, [eventId, email, sendCode, toast, tq]);

  return {
    formik,
    loading: formik.isSubmitting,
    resending,
    handleResend,
  };
};
